import { HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
const API_URL = process.env.REACT_APP_API_URL;

let hubConnection = null;
let chatReadyRef = { current: false };
let messageHandlers = [];
let refetchHandlers = [];
let groupRefetchHandlers = [];
let groupMessageHandlers = [];
let connectionUserId = null;
let currentApiUrl = null;
let initRetryCount = 0;
const MAX_INIT_RETRIES = 10;

// Helper to check if we can actually attempt a start
const canStart = (state) => state === HubConnectionState.Disconnected;

document.addEventListener("visibilitychange", async () => {
    if (document.visibilityState === "visible" && hubConnection) {
        if (!localStorage.getItem("storedToken")) return;
        if (canStart(hubConnection.state)) {
            try {
                console.log("🔄 Tab focused: Reconnecting SignalR...");
                await hubConnection.start();
                chatReadyRef.current = true;
            } catch (err) {
                console.log("Reconnect failed on focus", err);
            }
        }
    }
});



export const initHubConnection = async (userId, apiUrl) => {
    if (!userId || !apiUrl) return null;

    // 1. If user changed, wipe the old connection entirely
    if (hubConnection && connectionUserId !== userId) {
        await stopHubConnection();
        initRetryCount = 0;
    }

    connectionUserId = userId;
    currentApiUrl = apiUrl;

    // 2. Return existing if already active
    if (hubConnection?.state === HubConnectionState.Connected) return hubConnection;

    // 3. Block redundant attempts if already transitioning
    if (hubConnection?.state === HubConnectionState.Connecting ||
        hubConnection?.state === HubConnectionState.Reconnecting) {
        return hubConnection;
    }

    // 4. Create new connection if none exists
    if (!hubConnection) {
        hubConnection = new HubConnectionBuilder()
            .withUrl(`${apiUrl}/chathub/81?userId=${userId}&platform=web`, {
                withCredentials: false, // fine for cross-origin if your API allows
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (ctx) =>
                    ctx.elapsedMilliseconds < 60000 ? 5000 : 15000
            })
            .build();

        setupInternalListeners();
    }

    // 5. Start Connection
    if (canStart(hubConnection.state)) {
        try {
            await hubConnection.start();
            chatReadyRef.current = true;
            initRetryCount = 0;
            console.log("✅ SignalR connected");
            return hubConnection;
        } catch (err) {
            console.error("❌ SignalR start failed:", err);
            chatReadyRef.current = false;

            if (initRetryCount < MAX_INIT_RETRIES) {
                initRetryCount++;
                console.warn(`🔁 Init retry ${initRetryCount}/${MAX_INIT_RETRIES}`);
                setTimeout(() => {
                    if (connectionUserId === userId) initHubConnection(userId, apiUrl);
                }, 5000);
            } else {
                console.error("❌ Max init retries reached. Giving up.");
            }

            return null;
        }
    }
};

function setupInternalListeners() {
    if (!hubConnection) return;

    hubConnection.on("ParrotsChatHubInitialized", () => {
        chatReadyRef.current = true;
    });

    hubConnection.onreconnecting(() => {
        chatReadyRef.current = false;
    });

    hubConnection.onreconnected(() => {
        chatReadyRef.current = true;
    });

    hubConnection.on("ReceiveMessage", (data) => {
        messageHandlers.forEach(h => h(data));
    });

    hubConnection.on("ReceiveMessageRefetch", (data) => {
        refetchHandlers.forEach(h => h(data));
    });

    hubConnection.on("ReceiveGroupMessageRefetch", (groupConversationId) => {
        groupRefetchHandlers.forEach(h => h(groupConversationId));
    });

    hubConnection.on("ReceiveGroupMessage", (payload) => {
        groupMessageHandlers.forEach(h => h(payload));
    });

    hubConnection.onclose(() => {
        chatReadyRef.current = false;
        // Schedule re-init if user is still logged in (permanent disconnect recovery)
        if (connectionUserId && localStorage.getItem("storedToken")) {
            setTimeout(() => {
                if (connectionUserId && localStorage.getItem("storedToken")) {
                    console.log("🔁 Permanent disconnect — attempting re-init...");
                    initRetryCount = 0;
                    initHubConnection(connectionUserId, currentApiUrl);
                }
            }, 5000);
        }
    });
}

export const stopHubConnection = async () => {
    if (!hubConnection) return;
    try {
        // Remove SignalR internal subscriptions
        hubConnection.off("ReceiveMessage");
        hubConnection.off("ReceiveMessageRefetch");
        hubConnection.off("ReceiveGroupMessageRefetch");
        hubConnection.off("ReceiveGroupMessage");
        hubConnection.off("ParrotsChatHubInitialized");

        await hubConnection.stop();
        console.log("🔴 SignalR stopped");
    } catch (err) {
        console.error("❌ Error during stop:", err);
    } finally {
        hubConnection = null;
        messageHandlers = [];
        refetchHandlers = [];
        groupRefetchHandlers = [];
        groupMessageHandlers = [];
        chatReadyRef.current = false;
        connectionUserId = null;
    }
};

// --- Handler Registration (Standard Observer Pattern) ---

export const register_ReceiveMessage = (handler) => {
    if (!messageHandlers.includes(handler)) messageHandlers.push(handler);
};

export const unregister_ReceiveMessage = (handler) => {
    messageHandlers = messageHandlers.filter(h => h !== handler);
};

export const register_ReceiveMessageRefetch = (handler) => {
    if (!refetchHandlers.includes(handler)) refetchHandlers.push(handler);
};

export const unregister_ReceiveMessageRefetch = (handler) => {
    refetchHandlers = refetchHandlers.filter(h => h !== handler);
};

export const register_ReceiveGroupMessageRefetch = (handler) => {
    if (!groupRefetchHandlers.includes(handler)) groupRefetchHandlers.push(handler);
};

export const unregister_ReceiveGroupMessageRefetch = (handler) => {
    groupRefetchHandlers = groupRefetchHandlers.filter(h => h !== handler);
};

export const register_ReceiveGroupMessage = (handler) => {
    if (!groupMessageHandlers.includes(handler)) groupMessageHandlers.push(handler);
};

export const unregister_ReceiveGroupMessage = (handler) => {
    groupMessageHandlers = groupMessageHandlers.filter(h => h !== handler);
};

export const register_ReceiveUnreadNotification = (handler) => {
    if (!hubConnection) return;
    hubConnection.on("ReceiveUnreadNotification", handler);
};

export const unregister_ReceiveUnreadNotification = (handler) => {
    if (!hubConnection) return;
    hubConnection.off("ReceiveUnreadNotification", handler);
};

export const isHubReady = () => chatReadyRef.current && hubConnection?.state === HubConnectionState.Connected;

export const getHubState = () => {
    if (!hubConnection) return "disconnected";
    if (hubConnection.state === HubConnectionState.Connected && chatReadyRef.current) return "connected";
    if (hubConnection.state === HubConnectionState.Reconnecting) return "reconnecting";
    return "disconnected";
};

export const invokeHub = async (method, ...args) => {
    if (isHubReady()) {
        try {
            return await hubConnection.invoke(method, ...args);
        } catch (err) {
            console.warn(`⚠️ Invoke ${method} failed:`, err?.message ?? err);
            return null;
        }
    }
    console.warn(`⚠️ Cannot invoke ${method}: Hub not ready`);
};
